import { ExpandLess as ExpandLessIcon } from "@mui/icons-material";
import { Button, IconButton, Link, SvgIcon, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import GitHubIcon from "./GithubIcon";
import LinkedinIcon from "./LinkedinIcon";
import XIcon from "./XIcon";
import XingIcon from "./XingIcon";

import type { ComponentType, MouseEvent, SVGProps } from "react";

interface FooterLink {
  href?: string;
  label: string;
}

interface FooterProps {
  contactTitle?: string;
  links?: FooterLink[];
  onScrollToTop?: (evt: MouseEvent) => void;
}

const defaultProps = {
  contactTitle: "Contact",
  links: [
    { href: "https://geops.ch/about?language=en", label: "About geOps" },
    { href: "https://geops.ch/datenschutz", label: "Privacy Policy" },
    { href: "https://geops.ch/impressum", label: "Imprint" },
  ],
  onScrollToTop: () => {
    return window.scrollTo({ behavior: "smooth", left: 0, top: 0 });
  },
};

const createMediaIcon = (
  component: ComponentType<SVGProps<SVGSVGElement>>,
  props?: { fillRule?: SVGProps<SVGSVGElement>["fillRule"] },
) => {
  return (
    <SvgIcon
      component={component}
      style={{ height: 25, width: 25 }}
      viewBox="0 0 25 25"
      {...props}
    />
  );
};

const StyledFooter = styled("footer")(() => {
  return { position: "relative" };
});

const DivFooterMain = styled("div")(({ theme }) => {
  return {
    backgroundColor: theme.colors.footer,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    padding: "50px 8rem",
    [theme.breakpoints.down("lg")]: { padding: "50px 2rem" },

    [theme.breakpoints.down("sm")]: {
      alignItems: "initial",
      flexDirection: "column",
      minHeight: 100,
    },

    zIndex: 1100,
  };
});

const DivFooterElement = styled("div")(({ theme }) => {
  return {
    color: theme.colors.footerText,
    display: "flex",
    flexDirection: "column",
    fontSize: "1.125rem",
    justifyContent: "flex-start",
    margin: "15px 0",
    minWidth: 200,
  };
});

const DivImprint = styled("div")(({ theme }) => {
  return {
    alignItems: "center",
    backgroundColor: theme.colors.footerDark,
    color: theme.colors.footerText,
    display: "flex",
    flexWrap: "wrap",
    minHeight: 68,
    padding: "0 8rem",

    [theme.breakpoints.down("lg")]: { padding: "0 2rem" },
  };
});

const StyledLink = styled(Link)(({ theme }) => {
  return {
    "&:hover": { color: theme.colors.primaryGreen },
    color: theme.colors.footerText,

    fontSize: "1.125rem",
  };
});

const DivLinks = styled("div")(() => {
  return { display: "flex", flexDirection: "column", marginTop: 20 };
});

const ButtonSocialMediaBtn = styled(Button)(({ theme }) => {
  return {
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.colors.primaryGreen,
    },
    backgroundColor: "transparent",
    color: theme.colors.footerText,
    fontSize: "1.125rem",
    fontWeight: "normal",
    justifyContent: "flex-start",
    marginRight: "auto",

    padding: "0 0 10px",
  };
}) as typeof Button;

const IconButtonScrollToTop = styled(IconButton)(({ theme }) => {
  return {
    "& svg": { height: 80, width: 80 },
    color: theme.colors.footerText,
    position: "absolute",
    right: 10,

    top: 10,
  };
});

const DivContact = styled("div")(() => {
  return {
    "& p": { padding: "5px 0" },

    marginTop: 20,
  };
});

interface ContactInfo {
  address1: string;
  address2: string;
  mail: string;
  name: string;
  phone: string;
}

const renderContact = (contactProps: ContactInfo) => {
  return (
    <DivContact>
      <Typography variant="h6">
        <strong>{contactProps.name}</strong>
      </Typography>
      <br />
      <Typography>{contactProps.address1}</Typography>
      <Typography>{contactProps.address2}</Typography>
      <br />
      <Typography>
        fon:{" "}
        <StyledLink
          href={`tel: ${contactProps.phone}`}
          underline="none"
          variant="body1"
        >
          {contactProps.phone}
        </StyledLink>
      </Typography>
      <Typography>
        mail:{" "}
        <StyledLink
          href={`mailto: ${contactProps.mail}`}
          rel="noopener"
          target="_blank"
          underline="none"
          variant="body1"
        >
          {contactProps.mail}
        </StyledLink>
      </Typography>
    </DivContact>
  );
};

function Footer({
  contactTitle = defaultProps.contactTitle,
  links = defaultProps.links,
  onScrollToTop = defaultProps.onScrollToTop,
}: FooterProps) {
  return (
    <StyledFooter>
      <IconButtonScrollToTop
        onClick={(evt) => {
          return onScrollToTop(evt);
        }}
      >
        <ExpandLessIcon />
      </IconButtonScrollToTop>
      <DivFooterMain>
        <DivFooterElement>
          <Typography variant="h2">{contactTitle}</Typography>
        </DivFooterElement>
        <DivFooterElement>
          {renderContact({
            address1: "Solothurnerstrasse 235",
            address2: "CH-4600 Olten",
            mail: "info@geops.ch",
            name: "geOps AG",
            phone: "+41 61 588 05 05",
          })}
        </DivFooterElement>
        <DivFooterElement>
          {renderContact({
            address1: "Bismarkallee 10",
            address2: "D-79098 Freiburg",
            mail: "info@geops.ch",
            name: "geOps GmbH",
            phone: "+49 761 458 925 0",
          })}
        </DivFooterElement>
        <DivFooterElement>
          <DivLinks>
            <ButtonSocialMediaBtn
              component="a"
              href="https://github.com/geops/"
              rel="noopener"
              startIcon={createMediaIcon(GitHubIcon)}
              target="_blank"
            >
              GitHub
            </ButtonSocialMediaBtn>
            <ButtonSocialMediaBtn
              component="a"
              href="https://twitter.com/geops?lang=en"
              rel="noopener"
              startIcon={createMediaIcon(XIcon)}
              target="_blank"
            >
              X
            </ButtonSocialMediaBtn>
            <ButtonSocialMediaBtn
              component="a"
              href="https://www.xing.com/companies/geops"
              rel="noopener"
              startIcon={createMediaIcon(XingIcon)}
              target="_blank"
            >
              Xing
            </ButtonSocialMediaBtn>
            <ButtonSocialMediaBtn
              component="a"
              href="https://de.linkedin.com/company/geops"
              rel="noopener"
              startIcon={createMediaIcon(LinkedinIcon, { fillRule: "evenodd" })}
              target="_blank"
            >
              LinkedIn
            </ButtonSocialMediaBtn>
          </DivLinks>
        </DivFooterElement>
      </DivFooterMain>
      {Array.isArray(links) && links.length ? (
        <DivImprint>
          {links.map((link, idx, array) => {
            return (
              <div key={link.href}>
                <StyledLink href={link.href} underline="none" variant="body1">
                  {link.label}
                </StyledLink>
                {idx !== array.length - 1 && (
                  <span style={{ padding: "0 4px" }}>|</span>
                )}
              </div>
            );
          })}
        </DivImprint>
      ) : null}
    </StyledFooter>
  );
}

export default Footer;
